import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { useProject } from '../context/ProjectContext';

type AgentRunSummary = {
  run_id: string;
  mode: string;
  status: string;
  status_detail?: string | null;
  conversation_id?: number | null;
  ticket_key?: string | null;
  created_at?: string | null;
};

type Props = {
  ticketKey: string;
};

const statusLabel = (status: string) => {
  if (status === 'running' || status === 'pending' || status === 'queued') return 'běží';
  if (status === 'waiting_user') return 'čeká';
  if (status === 'success') return 'hotovo';
  if (status === 'failed') return 'selhalo';
  if (status === 'cancelled') return 'zrušeno';
  return status;
};

const statusClass = (status: string) => {
  if (status === 'running' || status === 'pending' || status === 'queued') return 'text-blue-600';
  if (status === 'waiting_user') return 'text-amber-600';
  if (status === 'success') return 'text-emerald-600';
  if (status === 'failed') return 'text-red-600';
  if (status === 'cancelled') return 'text-slate-500';
  return 'text-gray-500';
};

/**
 * Slim ticket controls: only Dev plan / Dev full + link into Complic chat.
 * Live AI / commands live in Complic chat, not here.
 */
const TicketAgentBar: React.FC<Props> = ({ ticketKey }) => {
  const { currentProject } = useProject();
  const [runs, setRuns] = useState<AgentRunSummary[]>([]);
  const [starting, setStarting] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/agents/runs`, {
        params: {
          project_id: currentProject?.id,
          ticket_key: ticketKey,
          limit: 8,
        },
      });
      setRuns(res.data.runs || []);
    } catch (e) {
      console.error(e);
    }
  }, [currentProject?.id, ticketKey]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 10000);
    return () => clearInterval(t);
  }, [refresh]);

  const openChat = (run?: AgentRunSummary) => {
    window.dispatchEvent(
      new CustomEvent('complic-open-conversation', {
        detail: {
          conversationId: run?.conversation_id,
          ticketKey: run?.ticket_key || ticketKey,
        },
      })
    );
  };

  const start = async (mode: 'plan' | 'full') => {
    if (!currentProject?.id) return;
    try {
      setStarting(mode);
      const res = await axios.post(`${API_URL}/api/agents/start`, {
        agent_id: 'developer',
        ticket_key: ticketKey,
        project_id: currentProject.id,
        mode,
        goal: `Developer ${mode} on ${ticketKey}`,
        open_conversation: true,
      });
      openChat({
        run_id: res.data.run_id,
        mode,
        status: res.data.queued ? 'queued' : 'running',
        conversation_id: res.data.conversation_id,
        ticket_key: ticketKey,
      });
      await refresh();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to start agent');
    } finally {
      setStarting(null);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          disabled={!!starting}
          onClick={() => start('plan')}
          className="px-2 py-1 text-[11px] font-sans rounded bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white"
        >
          {starting === 'plan' ? '…' : 'Dev plan'}
        </button>
        <button
          type="button"
          disabled={!!starting}
          onClick={() => start('full')}
          className="px-2 py-1 text-[11px] font-sans rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white"
        >
          {starting === 'full' ? '…' : 'Dev full'}
        </button>
      </div>

      {runs.length === 0 ? (
        <p className="text-[11px] text-gray-500">Zatím žádný běh — AI běží v Complic chatu.</p>
      ) : (
        <ul className="space-y-1 max-h-40 overflow-y-auto">
          {runs.map((r) => (
            <li
              key={r.run_id}
              className="flex items-center justify-between gap-2 rounded px-1.5 py-1 hover:bg-slate-50 dark:hover:bg-slate-800/50"
            >
              <div className="min-w-0">
                <p className="text-[11px] font-sans text-gray-800 dark:text-gray-100 truncate">
                  Dev {r.mode}
                  <span className={`ml-1.5 ${statusClass(r.status)}`}>
                    · {statusLabel(r.status)}
                  </span>
                </p>
                {r.status_detail && (
                  <p className="text-[10px] text-gray-500 truncate">{r.status_detail}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => openChat(r)}
                className="shrink-0 text-[11px] px-2 py-0.5 rounded bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-200"
              >
                Chat
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TicketAgentBar;
